import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Spin, 
  Alert, 
  Button, 
  Tabs, 
  Card, 
  Input, 
  Checkbox, 
  Select, 
  Table, 
  Space, 
  Typography,
  Tag
} from 'antd';
import { 
  CloudDownloadOutlined, 
  ReloadOutlined, 
  UnorderedListOutlined, 
  BarChartOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useGetDealsQuery, useGetStagesQuery, useSyncDealsMutation, useGetDealTasksQuery } from '../../redux/api/dealsApi';
import { getErrorMessage } from '../../redux/api/baseApi';
import { Pagination } from 'antd';
import DealStatistics from '../../components/deals/DealStatistics';

const { Title, Text } = Typography;

/**
 * Format currency value for display
 */
const formatCurrency = (value, currency = 'USD') => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '-';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numValue);
};

/**
 * Format date for display
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
};

/**
 * DealsPage - Display and manage Zendesk Sell deals
 */
const DealsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('statistics');

  // Read URL params with defaults
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = parseInt(searchParams.get('limit') || '10', 10);
  const searchParam = searchParams.get('search') || '';
  const hot = searchParams.get('hot') || '';
  const showInactive = searchParams.get('show_inactive') === 'true';
  const sortBy = searchParams.get('sort_by') || 'updated_at';
  const sortDirection = searchParams.get('sort_direction') || 'desc';

  // Local state for search input (debounced)
  const [searchInput, setSearchInput] = useState(searchParam);

  const updateSearchParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === undefined || value === false) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync local search input with URL param
  useEffect(() => {
    setSearchInput(searchParam);
  }, [searchParam]);

  // Debounce search input to URL params
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchParam) {
        updateSearchParams({ search: searchInput || null, page: 1 });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchParam, updateSearchParams]);

  // Fetch deals data with server-side pagination/filtering/sorting
  const { data, isLoading, error, refetch, isFetching } = useGetDealsQuery({
    page,
    limit,
    search: searchParam || undefined,
    hot: hot || undefined,
    show_inactive: showInactive,
    sort_by: sortBy,
    sort_direction: sortDirection,
  });
  
  // Fetch stages data (for statistics)
  const { data: stagesData, isLoading: stagesLoading } = useGetStagesQuery();
  
  // Sync mutation
  const [syncDeals, { isLoading: isSyncing }] = useSyncDealsMutation();
  
  // Sync state
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  
  // Local UI state (not persisted in URL)
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Extract deals and pagination from response
  const deals = useMemo(() => data?.deals || [], [data?.deals]);
  const pagination = useMemo(() => data?.pagination || {
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1
  }, [data?.pagination]);
  const stages = useMemo(() => stagesData?.stages || [], [stagesData?.stages]);

  // Handle page change
  const handlePageChange = (newPage) => {
    updateSearchParams({ page: newPage });
  };

  // Handle page size change
  const handlePageSizeChange = (newLimit) => {
    updateSearchParams({ limit: newLimit, page: 1 });
  };

  // Handle search input change (debounced via useEffect)
  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  // Handle filter changes
  const handleHotFilterChange = (checked) => {
    updateSearchParams({ hot: checked ? 'true' : null, page: 1 });
  };

  const handleShowInactiveChange = (checked) => {
    updateSearchParams({ show_inactive: checked ? 'true' : null, page: 1 });
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchParams({}, { replace: true });
  };

  // Component to render expanded row content (tasks for a deal)
  const DealTasksRow = ({ dealId }) => {
    const { data, isLoading, error } = useGetDealTasksQuery(dealId, {
      skip: !expandedRowKeys.includes(dealId),
    });

    if (isLoading) {
      return <div style={{ padding: '16px' }}><Spin size="small" /> Loading tasks...</div>;
    }

    if (error) {
      return <div style={{ padding: '16px', color: 'red' }}>Error loading tasks</div>;
    }

    const tasks = data?.tasks || [];

    if (tasks.length === 0) {
      return <div style={{ padding: '16px' }}>No tasks found for this deal.</div>;
    }

    return (
      <div style={{ padding: '16px' }}>
        <Table
          columns={[
            {
              title: 'Content',
              dataIndex: 'content',
              key: 'content',
              width: '30%',
            },
            {
              title: 'Status',
              key: 'status',
              width: '15%',
              render: (_, record) => (
                <Tag color={record.done_at ? 'green' : 'orange'}>
                  {record.done_at ? 'Completed' : 'Pending'}
                </Tag>
              ),
            },
            {
              title: 'Owner',
              dataIndex: 'owner',
              key: 'owner',
              width: '15%',
            },
            {
              title: 'Date',
              dataIndex: 'date',
              key: 'date',
              width: '15%',
              render: (date) => formatDate(date),
            },
            {
              title: 'Done At',
              dataIndex: 'done_at',
              key: 'done_at',
              width: '15%',
              render: (date) => date ? formatDate(date) : '-',
            },
            {
              title: 'Created',
              dataIndex: 'created_at',
              key: 'created_at',
              width: '15%',
              render: (date) => formatDate(date),
            },
          ]}
          dataSource={tasks}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </div>
    );
  };

  // Handle sync button click
  const handleSync = async () => {
    setSyncResult(null);
    setSyncError(null);
    
    try {
      const result = await syncDeals().unwrap();
      setSyncResult(result);
      
      // Auto-refresh deals list after successful sync
      if (result.success) {
        setTimeout(() => {
          refetch();
        }, 500);
      }
    } catch (err) {
      setSyncError(getErrorMessage(err));
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Spin size="large" tip="Loading deals..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        message="Error loading deals"
        description={error?.data?.error || error?.error || 'Failed to load deals from Zendesk Sell'}
        type="error"
        action={
          <Button size="small" danger onClick={() => refetch()}>
            Retry
          </Button>
        }
        showIcon
      />
    );
  }

  // Table columns configuration
  const columns = [
    {
      title: 'Deal Name',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
      sorter: true,
      sortOrder: sortBy === 'name' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (text) => <strong>{text || 'Unnamed Deal'}</strong>,
    },
    {
      title: 'Customer',
      dataIndex: 'linked_person',
      key: 'linked_person',
      width: '15%',
      render: (linkedPerson, record) => {
        if (!linkedPerson?.person_id) {
          // Show main_contact name from Zendesk if no linked person
          // But skip if it looks like just an ID (all digits)
          const hasValidName = record.main_contact && !/^\d+$/.test(record.main_contact);
          return hasValidName ? (
            <Text type="secondary">{record.main_contact}</Text>
          ) : (
            <Text type="secondary">-</Text>
          );
        }
        
        const displayName = [linkedPerson.first_name, linkedPerson.last_name]
          .filter(Boolean)
          .join(' ') || linkedPerson.email || 'Unknown';
        
        return (
          <Button
            type="link"
            icon={<UserOutlined />}
            onClick={() => navigate(`/persons/${linkedPerson.person_id}`)}
            style={{ padding: 0 }}
            title={linkedPerson.email || 'View customer details'}
          >
            {displayName}
          </Button>
        );
      },
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: '10%',
      sorter: true,
      sortOrder: sortBy === 'value' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (value, record) => formatCurrency(value, record.currency),
    },
    {
      title: 'Hot',
      dataIndex: 'hot',
      key: 'hot',
      width: '5%',
      align: 'center',
      render: (hot) => hot ? <span title="Hot Deal">🔥</span> : <Text type="secondary">-</Text>,
    },
    {
      title: 'Tasks',
      dataIndex: 'task_count',
      key: 'task_count',
      width: '8%',
      align: 'center',
      sorter: true,
      sortOrder: sortBy === 'task_count' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (count, record) => (
        <Button
          type="link"
          onClick={() => {
            if (expandedRowKeys.includes(record.id)) {
              setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.id));
            } else {
              setExpandedRowKeys([...expandedRowKeys, record.id]);
            }
          }}
          style={{ padding: 0 }}
        >
          {count || 0}
        </Button>
      ),
    },
    {
      title: 'Est. Close',
      dataIndex: 'estimated_close_date',
      key: 'estimated_close_date',
      width: '10%',
      sorter: true,
      sortOrder: sortBy === 'estimated_close_date' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (date) => formatDate(date),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '10%',
      sorter: true,
      sortOrder: sortBy === 'created_at' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (date) => formatDate(date),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: '10%',
      sorter: true,
      sortOrder: sortBy === 'updated_at' ? (sortDirection === 'asc' ? 'ascend' : 'descend') : null,
      render: (date) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '8%',
      render: (_, record) => (
        <Button
          type="link"
          href={`https://app.futuresimple.com/sales/deals/${record.id}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Open in Zendesk Sell"
        >
          View
        </Button>
      ),
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.field) {
      const key = sorter.field === 'value' ? 'value' : sorter.field;
      const direction = sorter.order === 'ascend' ? 'asc' : 'desc';
      updateSearchParams({ sort_by: key, sort_direction: direction, page: 1 });
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={1} style={{ margin: 0 }}>Zendesk Deals</Title>
        <Space>
          <Button 
            type="primary"
            icon={<CloudDownloadOutlined />}
            onClick={handleSync}
            loading={isSyncing}
          >
            Sync from Zendesk
          </Button>
          <Button 
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isFetching}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Sync Result Messages */}
      {syncResult && (
        <Alert
          message={syncResult.success ? '✓ Sync Completed' : '⚠ Sync Completed with Issues'}
          type={syncResult.success ? 'success' : 'warning'}
          closable
          onClose={() => setSyncResult(null)}
          style={{ marginBottom: 16 }}
          description={
            <div>
              {syncResult.summary && (
                <>
                  {/* Deals Summary */}
                  <div style={{ marginTop: 8 }}>
                    <strong>Deals:</strong>{' '}
                    {syncResult.summary.deals_fetched} fetched
                    {' • '}
                    {syncResult.summary.deals_inserted} new
                    {' • '}
                    {syncResult.summary.deals_updated} updated
                    {' • '}
                    {syncResult.summary.deals_skipped} skipped
                  </div>
                  {/* Tasks Summary */}
                  {syncResult.summary.tasks_fetched !== undefined && (
                    <div style={{ marginTop: 4 }}>
                      <strong>Tasks:</strong>{' '}
                      {syncResult.summary.tasks_fetched} fetched
                      {' • '}
                      {syncResult.summary.tasks_inserted} new
                      {' • '}
                      {syncResult.summary.tasks_updated} updated
                      {' • '}
                      {syncResult.summary.tasks_skipped} skipped
                    </div>
                  )}
                  {/* Errors Count */}
                  {syncResult.summary.errors > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <strong style={{ color: '#ff4d4f' }}>Errors:</strong> {syncResult.summary.errors}
                    </div>
                  )}
                </>
              )}
              {syncResult.errors && syncResult.errors.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong>Error Details:</strong>
                  <ul style={{ marginBottom: 0, marginTop: 4 }}>
                    {syncResult.errors.slice(0, 5).map((error, idx) => (
                      <li key={idx} style={{ fontSize: '12px' }}>{error}</li>
                    ))}
                    {syncResult.errors.length > 5 && (
                      <li style={{ fontSize: '12px', color: '#8c8c8c' }}>... and {syncResult.errors.length - 5} more errors</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          }
        />
      )}

      {syncError && (
        <Alert
          message="Sync Failed"
          description={syncError}
          type="error"
          closable
          onClose={() => setSyncError(null)}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'list',
            label: (
              <span>
                <UnorderedListOutlined />
                {' '}Deals List
              </span>
            ),
          },
          {
            key: 'statistics',
            label: (
              <span>
                <BarChartOutlined />
                {' '}Statistics
              </span>
            ),
          },
        ]}
        style={{ marginBottom: 24 }}
      />

      {/* Tab Content */}
      {activeTab === 'list' && (
        <>
          {/* Filters Row */}
          <Card style={{ marginBottom: 24 }}>
            <Space orientation="vertical" style={{ width: '100%' }} size="middle">
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {/* Search */}
                <div style={{ flex: '1 1 300px' }}>
                  <Text strong>Search</Text>
                  <Input
                    placeholder="Search by deal name, contact, company, owner..."
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    allowClear
                    onClear={() => handleSearchChange('')}
                    style={{ marginTop: 4 }}
                  />
                </div>
                
                {/* Hot Deals Filter */}
                <div>
                  <Checkbox
                    checked={hot === 'true'}
                    onChange={(e) => handleHotFilterChange(e.target.checked)}
                  >
                    Hot Deals Only
                  </Checkbox>
                </div>
                
                {/* Include Inactive Deals Filter */}
                <div>
                  <Checkbox
                    checked={showInactive}
                    onChange={(e) => handleShowInactiveChange(e.target.checked)}
                  >
                    Include Inactive Deals
                  </Checkbox>
                </div>
                
                {/* Items per page */}
                <div style={{ width: 150 }}>
                  <Text strong>Show</Text>
                  <Select
                    value={limit}
                    onChange={handlePageSizeChange}
                    style={{ width: '100%', marginTop: 4 }}
                    options={[
                      { value: 10, label: '10 per page' },
                      { value: 25, label: '25 per page' },
                      { value: 50, label: '50 per page' },
                      { value: 100, label: '100 per page' },
                    ]}
                  />
                </div>
                
                {/* Reset Filters */}
                <div>
                  <Button onClick={handleResetFilters}>
                    Reset Filters
                  </Button>
                </div>
              </div>
            </Space>
          </Card>

          {/* Results count */}
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Showing {deals.length} of {pagination.total} deals
              {searchParam && ` matching "${searchParam}"`}
              {hot === 'true' && ' (hot deals only)'}
              {showInactive && ' (including inactive)'}
            </Text>
          </div>

          {/* Deals Table */}
          {deals.length === 0 ? (
            <Alert
              message="No deals found"
              description={searchParam || hot === 'true' ? 'No deals match your filters.' : 'No deals available.'}
              type="info"
              showIcon
            />
          ) : (
            <Table
              columns={columns}
              dataSource={deals}
              rowKey="id"
              pagination={false}
              onChange={handleTableChange}
              size="middle"
              expandable={{
                expandedRowKeys: expandedRowKeys,
                onExpandedRowsChange: (expandedKeys) => {
                  setExpandedRowKeys(expandedKeys);
                },
                expandedRowRender: (record) => <DealTasksRow dealId={record.id} />,
                rowExpandable: (record) => (record.task_count || 0) > 0,
              }}
            />
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="mt-4">
              <Pagination
                current={pagination.current_page}
                total={pagination.total}
                pageSize={pagination.per_page}
                onChange={handlePageChange}
                hideOnSinglePage={true}
                showSizeChanger={false}
                showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} deals`}
              />
            </div>
          )}
        </>
      )}
      
      {activeTab === 'statistics' && (
        <DealStatistics 
          deals={deals} 
          stages={stages} 
          isLoading={isLoading || stagesLoading} 
        />
      )}
    </div>
  );
};

export default DealsPage;
